import React, { useCallback } from 'react';
import { FormControl, FormHelperText } from '@mui/material';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { titleCase } from '@utils/string-util';

import type { StripeCardFieldProps } from './types';

import classes from './stripe-card-field.module.css';

export const StripeCardField: React.FC<StripeCardFieldProps> = ({
  error,
  onChange,
  onComplete,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = React.useState<string | null>(null);

  const handleCardChange = useCallback(
    async (event: StripeCardElementChangeEvent) => {
      setCardError(event.error ? event.error.message : null);

      // Call onChange handler for real-time updates (optional)
      if (onChange) {
        if (event.complete && !event.error) {
          if (!stripe || !elements) {
            onChange(null);
            return;
          }

          try {
            const cardElement = elements.getElement(CardElement);
            if (!cardElement) {
              onChange(null);
              return;
            }

            const { paymentMethod, error: paymentMethodError } =
              await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
              });

            if (paymentMethodError) {
              onChange(null);
            } else if (paymentMethod?.id) {
              onChange(paymentMethod.id);
            }
          } catch (err: unknown) {
            console.error('Error creating payment method:', err);
            onChange(null);
          }
        } else {
          onChange(null);
        }
      }

      // Call onComplete handler when card is complete and valid
      if (event.complete && !event.error && onComplete) {
        if (!stripe || !elements) {
          setCardError('Stripe is not loaded');
          return;
        }

        try {
          const cardElement = elements.getElement(CardElement);
          if (!cardElement) {
            setCardError('Card element not found');
            return;
          }

          const { paymentMethod, error: paymentMethodError } =
            await stripe.createPaymentMethod({
              type: 'card',
              card: cardElement,
            });

          if (paymentMethodError) {
            setCardError(paymentMethodError.message || 'An error occurred');
          } else if (paymentMethod?.id) {
            onComplete(
              paymentMethod.id,
              paymentMethod.card
                ? `${titleCase(paymentMethod.card.brand)}...${
                    paymentMethod.card.last4
                  }`
                : 'New card'
            );
          }
        } catch (err: unknown) {
          const errorMessage =
            err instanceof Error
              ? err.message
              : 'An error occurred creating the payment method';
          setCardError(errorMessage);
        }
      }
    },
    [onChange, onComplete, stripe, elements]
  );

  return (
    <FormControl error={!!error || !!cardError} fullWidth>
      <div className={classes['stripe-card-container']}>
        <CardElement
          onChange={handleCardChange}
          options={{
            style: {
              base: {
                color: '#424770',
                fontSize: '16px',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>
      {(error || cardError) && (
        <FormHelperText>{error || cardError}</FormHelperText>
      )}
    </FormControl>
  );
};
